import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  BarChart3,
  TrendingUp,
  PieChart,
  Activity,
  Target,
  AlertTriangle,
  DollarSign,
  Clock,
  Zap,
  Shield,
  Target as TargetIcon,
  TrendingDown,
  Home,
  Briefcase,
  Settings,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from 'recharts';

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [timeframe, setTimeframe] = useState('1M');
  const [selectedMetric, setSelectedMetric] = useState('performance');

  const timeframes = ['1W', '1M', '3M', '6M', '1Y'];

  // Enhanced mock analytics data
  const performanceData = [
    { date: 'Jan 1', value: 100000, pnl: 0, trades: 0, volume: 0 },
    { date: 'Jan 8', value: 105000, pnl: 5000, trades: 12, volume: 45000 },
    { date: 'Jan 15', value: 110000, pnl: 10000, trades: 28, volume: 89000 },
    { date: 'Jan 22', value: 108000, pnl: 8000, trades: 35, volume: 125000 },
    { date: 'Jan 29', value: 115000, pnl: 15000, trades: 42, volume: 167000 },
    { date: 'Feb 5', value: 120000, pnl: 20000, trades: 51, volume: 203000 },
    { date: 'Feb 12', value: 125000, pnl: 25000, trades: 58, volume: 245000 },
  ];

  const tradingMetrics = [
    { metric: 'Win Rate', value: '68.5%', change: '+2.3%', isPositive: true, icon: TargetIcon },
    { metric: 'Profit Factor', value: '1.85', change: '+0.15', isPositive: true, icon: TrendingUp },
    { metric: 'Average Win', value: '$1,250', change: '+$45', isPositive: true, icon: DollarSign },
    { metric: 'Average Loss', value: '$850', change: '-$32', isPositive: true, icon: TrendingDown },
    { metric: 'Max Drawdown', value: '8.2%', change: '-1.1%', isPositive: true, icon: AlertTriangle },
    { metric: 'Sharpe Ratio', value: '1.42', change: '+0.08', isPositive: true, icon: Activity },
    { metric: 'Total Trades', value: '58', change: '+7', isPositive: true, icon: Clock },
    { metric: 'Avg Hold Time', value: '2.3h', change: '-0.2h', isPositive: true, icon: Zap },
  ];

  const assetAllocation = [
    { name: 'Cryptocurrency', value: 45, color: '#2AB7A6', change: '+2.1%' },
    { name: 'Forex', value: 25, color: '#10B981', change: '+1.8%' },
    { name: 'Commodities', value: 15, color: '#F59E0B', change: '-0.5%' },
    { name: 'Stocks', value: 10, color: '#8B5CF6', change: '+0.3%' },
    { name: 'Indices', value: 5, color: '#EF4444', change: '-0.2%' },
  ];

  const riskMetrics = [
    { metric: 'VaR (95%)', value: '$8,450', level: 'medium', change: '-$320' },
    { metric: 'Expected Shortfall', value: '$12,300', level: 'medium', change: '-$450' },
    { metric: 'Portfolio Beta', value: '0.85', level: 'low', change: '-0.02' },
    { metric: 'Correlation', value: '0.32', level: 'low', change: '-0.05' },
    { metric: 'Volatility', value: '12.5%', level: 'medium', change: '-0.8%' },
    { metric: 'Sortino Ratio', value: '1.68', level: 'high', change: '+0.12' },
  ];

  const tradeDistribution = [
    { range: '0-1h', count: 15, percentage: 25.9 },
    { range: '1-4h', count: 22, percentage: 37.9 },
    { range: '4-24h', count: 12, percentage: 20.7 },
    { range: '1-7d', count: 6, percentage: 10.3 },
    { range: '7d+', count: 3, percentage: 5.2 },
  ];

  const COLORS = ['#2AB7A6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];

  const metricTabs = [
    { id: 'performance', name: 'Performance', icon: TrendingUp },
    { id: 'risk', name: 'Risk Analysis', icon: Shield },
    { id: 'trades', name: 'Trade Analysis', icon: BarChart3 },
  ];

  const navigationMenu = [
    { name: 'Dashboard', href: '/miai', icon: Home, current: false },
    { name: 'Portfolio', href: '/portfolio', icon: Briefcase, current: false },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, current: true },
    { name: 'Settings', href: '/settings', icon: Settings, current: false },
  ];

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      {/* Navigation Menu */}
      <div className="card p-4 flex-shrink-0">
        <nav className="flex space-x-8">
          {navigationMenu.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.name}
                onClick={() => navigate(item.href)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  item.current
                    ? 'bg-[#2AB7A6] text-black shadow-lg'
                    : 'theme-text-quaternary hover:theme-text-secondary hover:theme-bg-quaternary'
                }`}
              >
                <Icon className={`h-4 w-4 ${item.current ? 'text-black' : 'theme-text-quaternary'}`} />
                <span>{item.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="space-y-6 p-6">
          {/* Enhanced Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold theme-text-primary flex items-center">
                <BarChart3 className="h-6 w-6 mr-3 text-[#2AB7A6]" />
                Analytics
              </h1>
              <p className="theme-text-quaternary mt-2">
                Analyze your trading performance, risk metrics, and market insights
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <div className={`flex rounded-lg ${theme === 'light' ? 'bg-gray-200' : 'bg-gray-700'} p-1`}>
                {timeframes.map(tf => (
                  <button
                    key={tf}
                    onClick={() => setTimeframe(tf)}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                      timeframe === tf
                        ? 'bg-[#2AB7A6] text-white'
                        : theme === 'light' ? 'text-gray-600 hover:text-gray-800' : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {tf}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Metric Tabs */}
          <div className={`flex space-x-1 ${theme === 'light' ? 'bg-gray-200' : 'bg-gray-800'} p-1 rounded-lg`}>
            {metricTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedMetric(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    selectedMetric === tab.id
                      ? 'bg-[#2AB7A6] text-white'
                      : theme === 'light' ? 'text-gray-600 hover:text-gray-800' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>

          {/* Performance Overview */}
          {selectedMetric === 'performance' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="card p-6">
                  <h2 className="text-lg font-semibold theme-text-primary mb-6 flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                    Portfolio Performance
                  </h2>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={performanceData}>
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2AB7A6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#2AB7A6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme === 'light' ? '#e5e7eb' : '#374151'} />
                        <XAxis dataKey="date" stroke={theme === 'light' ? '#6b7280' : '#9CA3AF'} />
                        <YAxis stroke={theme === 'light' ? '#6b7280' : '#9CA3AF'} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme === 'light' ? '#ffffff' : '#1F2937',
                            border: `1px solid ${theme === 'light' ? '#e5e7eb' : '#374151'}`,
                            borderRadius: '8px',
                            color: theme === 'light' ? '#111827' : '#F9FAFB',
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#2AB7A6"
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#colorValue)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="space-y-6">
                <div className="card p-4">
                  <h3 className="text-sm font-semibold theme-text-primary mb-4">Quick Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm theme-text-quaternary">Total Return</span>
                      <span className="text-lg font-bold text-[#2AB7A6]">+25.0%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm theme-text-quaternary">Total P&L</span>
                      <span className="text-lg font-bold text-[#2AB7A6]">+$25,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm theme-text-quaternary">Total Volume</span>
                      <span className="text-sm font-medium theme-text-primary">$245,000</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm theme-text-quaternary">Best Day</span>
                      <span className="text-sm font-medium text-[#2AB7A6]">+$3,200</span>
                    </div>
                  </div>
                </div>

                <div className="card p-4">
                  <h3 className="text-sm font-semibold theme-text-primary mb-4">Asset Allocation</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={assetAllocation}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={60}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {assetAllocation.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme === 'light' ? '#ffffff' : '#1F2937',
                            border: `1px solid ${theme === 'light' ? '#e5e7eb' : '#374151'}`,
                            borderRadius: '8px',
                            color: theme === 'light' ? '#111827' : '#F9FAFB',
                          }}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trading Metrics Grid */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold theme-text-primary mb-6 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-[#2AB7A6]" />
              Trading Metrics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tradingMetrics.map((metric, index) => {
                const Icon = metric.icon;
                return (
                  <div key={index} className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'} hover:border-[#2AB7A6] transition-colors duration-200`}>
                    <div className="flex items-center justify-between mb-3">
                      <Icon className="h-5 w-5 text-[#2AB7A6]" />
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${
                          metric.isPositive ? 'text-[#2AB7A6] bg-[#2AB7A6]/10' : 'text-red-400 bg-red-400/10'
                        }`}
                      >
                        {metric.change}
                      </span>
                    </div>
                    <div className="text-sm theme-text-quaternary mb-1">{metric.metric}</div>
                    <div className="text-xl font-bold theme-text-primary">{metric.value}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Risk Analysis */}
          {selectedMetric === 'risk' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card p-6">
                <h2 className="text-lg font-semibold theme-text-primary mb-6 flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                  Risk Metrics
                </h2>
                <div className="space-y-4">
                  {riskMetrics.map((metric, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-4 ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            metric.level === 'low'
                              ? 'bg-[#2AB7A6]'
                              : metric.level === 'medium'
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                          }`}
                        ></div>
                        <span className="text-sm font-medium theme-text-secondary">
                          {metric.metric}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold theme-text-primary">
                          {metric.value}
                        </div>
                        <div className="text-xs theme-text-quaternary">{metric.change}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-6">
                <h2 className="text-lg font-semibold theme-text-primary mb-6 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                  Drawdown Analysis
                </h2>
                <div className="space-y-4">
                  <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                    <h3 className="text-sm font-medium theme-text-secondary mb-3">Current Drawdown</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm theme-text-quaternary">Current:</span>
                        <span className="text-sm theme-text-primary">2.3%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm theme-text-quaternary">Max:</span>
                        <span className="text-sm theme-text-primary">8.2%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm theme-text-quaternary">Recovery Time:</span>
                        <span className="text-sm theme-text-primary">12 days</span>
                      </div>
                    </div>
                  </div>

                  <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                    <h3 className="text-sm font-medium theme-text-secondary mb-3">Volatility Metrics</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm theme-text-quaternary">Portfolio:</span>
                        <span className="text-sm theme-text-primary">12.5%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm theme-text-quaternary">Market:</span>
                        <span className="text-sm theme-text-primary">15.2%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm theme-text-quaternary">Beta:</span>
                        <span className="text-sm theme-text-primary">0.85</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trade Analysis */}
          {selectedMetric === 'trades' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card p-6">
                <h2 className="text-lg font-semibold theme-text-primary mb-6 flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                  Trade Distribution
                </h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={tradeDistribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme === 'light' ? '#e5e7eb' : '#374151'} />
                      <XAxis dataKey="range" stroke={theme === 'light' ? '#6b7280' : '#9CA3AF'} />
                      <YAxis stroke={theme === 'light' ? '#6b7280' : '#9CA3AF'} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: theme === 'light' ? '#ffffff' : '#1F2937',
                          border: `1px solid ${theme === 'light' ? '#e5e7eb' : '#374151'}`,
                          borderRadius: '8px',
                          color: theme === 'light' ? '#111827' : '#F9FAFB',
                        }}
                      />
                      <Bar dataKey="count" fill="#2AB7A6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card p-6">
                <h2 className="text-lg font-semibold theme-text-primary mb-6 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-[#2AB7A6]" />
                  Trade Statistics
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                      <div className="text-2xl font-bold text-[#2AB7A6]">58</div>
                      <div className="text-sm theme-text-quaternary">Total Trades</div>
                    </div>
                    <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                      <div className="text-2xl font-bold text-[#2AB7A6]">40</div>
                      <div className="text-sm theme-text-quaternary">Winning Trades</div>
                    </div>
                    <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                      <div className="text-2xl font-bold text-red-400">18</div>
                      <div className="text-sm theme-text-quaternary">Losing Trades</div>
                    </div>
                    <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg p-4 border ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'}`}>
                      <div className="text-2xl font-bold theme-text-primary">2.3h</div>
                      <div className="text-sm theme-text-quaternary">Avg Hold Time</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Add padding at the end */}
        <div className="pb-[50px]"></div>
      </div>
    </div>
  );
};

export default Analytics;
